/**
 * Prompt Format Detector
 *
 * Detects the format of a prompt based on its content.
 * Detection order: structured → free text
 *
 * This module provides retrofittable detection logic that can be easily
 * modified in the future as prompt formats evolve.
 */

/**
 * Structured format markers (legacy format)
 */
const STRUCTURED_MARKERS = [
  'Context:',
  'Task:',
  'Requirements:',
  'Constraints:',
  'Format your response as:',
  'Examples/Reference:'
];

/**
 * Check if promptText matches structured format (legacy persona/task format)
 * @param {string} promptText - The prompt text to check
 * @returns {boolean} True if text matches structured format
 */
export const isStructuredFormat = (promptText) => {
  if (!promptText || typeof promptText !== 'string') {
    return false;
  }

  const text = promptText.trim();

  // Check for structured format markers
  // Need at least a few markers to be considered structured
  const markerCount = STRUCTURED_MARKERS.filter(marker =>
    text.includes(marker)
  ).length;

  if (markerCount > 0) {
    return true;
  }

  // Also check for persona pattern (first line without a prefix)
  // Structured format often starts with persona (no prefix)
  const lines = text.split('\n\n');
  if (lines.length > 0) {
    const firstLine = lines[0].trim();
    // If first line doesn't start with any known marker, it might be persona
    const startsWithMarker = STRUCTURED_MARKERS.some(marker =>
      firstLine.startsWith(marker)
    );

    if (!startsWithMarker && firstLine.length > 0 && markerCount > 0) {
      return true;
    }
  }

  return false;
};

/**
 * Check if promptText is free text (doesn't match structured patterns)
 * @param {string} promptText - The prompt text to check
 * @returns {boolean} True if text appears to be free text
 */
export const isFreeText = (promptText) => {
  if (!promptText || typeof promptText !== 'string') {
    return false;
  }

  const text = promptText.trim();

  // If it matches structured format, it's not free text
  if (isStructuredFormat(text)) {
    return false;
  }

  // Free text is any text that doesn't follow structured patterns
  const hasAnyMarkers = STRUCTURED_MARKERS.some(marker =>
    text.includes(marker)
  );

  return !hasAnyMarkers;
};

/**
 * Detect the format of a prompt
 * Detection order: free text → structured format
 *
 * @param {Object|string} prompt - Prompt object with promptText property, or promptText string
 * @returns {string} Format type: 'FREE_TEXT' or 'STRUCTURED'
 */
export const detectPromptFormat = (prompt) => {
  // Handle both object and string inputs
  const promptText = typeof prompt === 'string' ? prompt : (prompt?.promptText || '');

  // If promptFormat is explicitly set, use it (but validate)
  if (prompt && typeof prompt === 'object' && prompt.promptFormat) {
    const format = prompt.promptFormat.toUpperCase();
    if (['FREE_TEXT', 'STRUCTURED'].includes(format)) {
      return format;
    }
  }

  // Detection order: free text → structured format
  if (isFreeText(promptText)) {
    return 'FREE_TEXT';
  }

  if (isStructuredFormat(promptText)) {
    return 'STRUCTURED';
  }

  // Default to free text as the standard format
  return 'FREE_TEXT';
};

/**
 * Get the default format for new prompts
 * @returns {string} Default format ('FREE_TEXT' - the standard format)
 */
export const getDefaultFormat = () => {
  return 'FREE_TEXT';
};
